'use es6';

import { Map as ImmutableMap, fromJS } from 'immutable';
import { createSelector } from 'reselect';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { getIsTransactional, getSubscriptionGroups, getPreferencesGroupId } from 'EmailEditorUI/redux/selectors/subscriptionSelectors';
import { emailConfigSelector } from 'EmailEditorUI/redux/selectors/sharedSelectors';
import { getAllUsersWithCurrentUser, getLanguage } from 'EmailEditorUI/redux/selectors/EmailSelectors';
import { getABTest } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { getMaximumRateLimitOption } from 'EmailEditorUI/StaggerSends/selectors/StaggerSendsSelectors';
import { getHasEmailThrottleSendSpeedAccess } from 'EmailEditorUI/StaggerSends/selectors/AuthSelectors';
import { getSendSpeedError } from 'EmailEditorUI/StaggerSends/selectors/SendRateSelectors';
import CanSpamSetting from 'EmailEditorUI/redux/models/CanSpamSetting';
import { getModulesWithSmartContent } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { stringContainsEmoji } from 'EmailEditorUI/utils/emojiUtils';
import { getSiteSettings } from 'ContentEditorUI/redux/selectors/siteSettingsSelectors';
import { getCanHaveMultipleOfficeLocations, usingEmailStarter, getHasFullEditorAccess, getHasCoreContentAccess, getHasSubscriptionGroups, getHasSubscriptionGroupsWriteAccess, getIsUngatedForAllowPersonalInboxes } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { getSingleRecipient } from './RecipientsSelectors';
import { createContentModelSelector, getContentModel } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getUserEmail } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getFromAddresses } from 'EmailData/selectors/emailAddressOwnershipSelectors';
import { getUseRssHeadlineAsSubject } from 'EmailEditorUI/redux/selectors/BlogSelectors';
import { canSpamAsString } from 'EmailEditorUI/utils/canSpam';
import { getIsUngatedForBEValidation } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { getFieldErrorMap } from 'EmailEditorUI/redux/selectors/ApiValidationSelectors';
import { STAGGER_EMAIL_SEND_SPEED, SEND_SPEED_ERRORS } from 'EmailEditorUI/StaggerSends/constants';
import Raven from 'raven-js';
import { getIsSimpleAbTest } from 'EmailEditorUI/Experimentation/selectors/simpleAbTest';
import * as FromFieldConstants from 'EmailData/constants/FromFieldConstants';
export const createDomainsRegex = (domains = []) => new RegExp(`(${domains.map(d => d[0] === '@' ? d : `@${d}`).join('|')})`, 'i');

// delete with Email:FromFieldsRefactoring clean up
export const isToken = val => /{{.*}}/.test(val);

//SELECTORS FOR FIELD DATA

export const getReplyTo = createContentModelSelector('replyTo');
export const getReplyToUserId = createContentModelSelector('replyToUserId');
export const getCustomReplyTo = createContentModelSelector('customReplyTo');
export const getCustomReplyToUserId = createContentModelSelector('customReplyToUserId');
export const getCustomReplyToEnabled = createContentModelSelector('customReplyToEnabled');
export const getFromName = createContentModelSelector('fromName');
export const getSubject = createContentModelSelector('subject');
export const getEmailNameValue = createContentModelSelector('name');
export const getEmailBusinessUnitId = createContentModelSelector('businessUnitId');
export const getGeneratedPlaintext = emailConfigSelector('generatedPlaintext');
export const getSavedPlaintext = createContentModelSelector('emailbodyPlaintext');
export const getTeamPerms = createContentModelSelector('teamPerms');
export const getUserPerms = createContentModelSelector('userPerms');
export const getIsUsingCustomPlaintext = createSelector(getSavedPlaintext, saved => saved.length > 0);
export const getCurrentPlaintextValue = createSelector([getSavedPlaintext, getGeneratedPlaintext], (saved, generated) => {
  return saved || generated;
});
export const getIsWebversionEnabledOnContentModel = createContentModelSelector('createPage');
export const getSmartRules = createContentModelSelector('smartEmailFields');
export const getSmartSubjectDefinition = createSelector([getSmartRules], smartRules => (smartRules === null || smartRules === void 0 ? void 0 : smartRules.SUBJECT) || null);
export const getSmartSubjectRulesCount = createSelector([getSmartSubjectDefinition], smartSubject => {
  var _smartSubject$smart_o;
  if (!smartSubject || !(smartSubject !== null && smartSubject !== void 0 && smartSubject.smart_objects)) {
    return 0;
  }
  return smartSubject === null || smartSubject === void 0 || (_smartSubject$smart_o = smartSubject.smart_objects) === null || _smartSubject$smart_o === void 0 ? void 0 : _smartSubject$smart_o.length;
});
export const getSubjectHasSmartContent = createSelector([getSmartSubjectRulesCount], count => count > 0);
export const getSubjectHasEmoji = createSelector([getSubject, getSmartSubjectDefinition], (subject, definition) => {
  var _definition$smart_obj;
  if (stringContainsEmoji(subject)) {
    return true;
  }
  return (definition === null || definition === void 0 || (_definition$smart_obj = definition.smart_objects) === null || _definition$smart_obj === void 0 ? void 0 : _definition$smart_obj.some(rule => stringContainsEmoji(rule.value))) || false;
});
export const getSmartModulesForEmail = createSelector([getModulesWithSmartContent, getSmartRules], (modules, emailModules) => {
  return ImmutableMap(modules).merge(fromJS(emailModules));
});

// delete with Email:FromFieldsRefactoring clean up
const getCustomFromNames = createSelector([emailConfigSelector('customFromNames')], replyTos => {
  return replyTos.map(fullName => ({
    fullName,
    type: FromFieldConstants.CUSTOM
  }));
});
// delete with Email:FromFieldsRefactoring clean up
const getCanUseMergeTagsInReplyTo = createSelector(getHasCoreContentAccess, hasCoreContentAccess => hasCoreContentAccess);
// delete with Email:FromFieldsRefactoring clean up
export const getFromNameOptions = createSelector([getAllUsersWithCurrentUser, getCustomFromNames, getCanUseMergeTagsInReplyTo], (hubspotUsers, customFromNames, canUseMergeTags) => {
  const result = {};
  if (canUseMergeTags) {
    result[FromFieldConstants.MERGE_TAG] = [{
      text: '{{owner.fullname}}',
      type: FromFieldConstants.MERGE_TAG,
      value: '{{owner.fullname}}'
    }];
  }
  const funcForType = type => u => {
    const value = u.fullName;
    if (canUseMergeTags && isToken(value) && !result[FromFieldConstants.MERGE_TAG].find(m => m.text === value)) {
      result[FromFieldConstants.MERGE_TAG].push({
        text: value,
        type: FromFieldConstants.MERGE_TAG,
        value
      });
    } else if (value) {
      const option = {
        text: value,
        type,
        value
      };
      if (result[type]) {
        result[type].push(option);
      } else {
        result[type] = [option];
      }
    }
  };
  hubspotUsers.forEach(funcForType(FromFieldConstants.USER));
  customFromNames.forEach(funcForType(FromFieldConstants.CUSTOM));
  return result;
});
export const getMaskedFreemailDomains = emailConfigSelector('maskedFreemailDomains');
export const testEmailAgainstDomains = (email = '', domains) => Boolean(email.match(createDomainsRegex(domains)));
export const getMaskedFreemailRewriteDomain = createSelector([usingEmailStarter, getHasFullEditorAccess], (isUsingEmailStarter, hasFullEditorAccess) => {
  if (hasFullEditorAccess) {
    return 'hubspotemail.net';
  } else if (isUsingEmailStarter) {
    return 'hubspotstarter.net';
  } else {
    return 'hubspotfree.net';
  }
});
const getMaskedEmailAddress = (address, domain) => `${address.replace('@', '.')}@${domain}`;
export const createMaskedAddressSelector = getAddress => createSelector([getAddress, getMaskedFreemailDomains, getMaskedFreemailRewriteDomain], (address, domains, rewriteDomain) => {
  if (testEmailAgainstDomains(address, domains)) {
    return getMaskedEmailAddress(address, rewriteDomain);
  }
  return address;
});
export const getMaskedReplyTo = createMaskedAddressSelector(getReplyTo);
export const getReplyToRequiresMasking = createSelector([getReplyTo, getMaskedFreemailDomains], testEmailAgainstDomains);
export const getReplyTos = createSelector([getFromAddresses, getCanUseMergeTagsInReplyTo, getIsUngatedForAllowPersonalInboxes], (fromAddresses, canUseMergeTagsInReplyTo, isUngatedForAllowPersonalInboxes) => {
  const builtInOptions = canUseMergeTagsInReplyTo ? [{
    text: '{{owner.email}}',
    value: '{{owner.email}}',
    type: FromFieldConstants.MERGE_TAG
  }] : [];
  return [...builtInOptions, ...fromAddresses.map(addr => {
    const value = isUngatedForAllowPersonalInboxes && addr.get('userId') ? `${addr.get('email')}_${addr.get('userId')}` : addr.getEmail();
    const email = addr.get('email');
    const userId = addr.get('userId');
    const type = addr.get('type');
    const help = addr.get('help');
    return Object.assign({
      text: email,
      value
    }, isUngatedForAllowPersonalInboxes ? {
      email,
      help
    } : {}, {
      isVerified: addr.getIsVerified(),
      type,
      userId
    });
  })];
});
export const getReplyToOptions = createSelector([getReplyTos], replyTos => {
  return replyTos.reduce((groups, item) => {
    const type = item.type;
    if (groups[type]) {
      groups[type].push(item);
    } else {
      groups[type] = [item];
    }
    return groups;
  }, {});
});
export const getReplyToDetails = createSelector([getFromAddresses, (state, address) => address], (fromAddresses, address) => {
  return fromAddresses && fromAddresses.find(a => a.get('email') === address);
});
export const getReplyToRequiresVerification = createSelector([getReplyToDetails], address => address && !address.getIsVerified());
export const getReplyToIsPendingVerification = createSelector([getReplyToDetails], address => address && address.getIsPendingVerification());
export const getNonGroupCanSpamSettings = createContentModelSelector('canSpamSettings');
export const getCanSpamSettingsId = createContentModelSelector('canSpamSettingsId');
export const getCanSpamSettings = createSelector([getHasSubscriptionGroups, getSubscriptionGroups, getNonGroupCanSpamSettings, getPreferencesGroupId, getCanSpamSettingsId], (ungatedForGroups, groups, nonGroupCanSpamSettings, selectedGroupId, selectedCanSpamId) => {
  if (!ungatedForGroups) {
    return nonGroupCanSpamSettings;
  }
  if (!selectedGroupId || groups.length === 0) {
    // could have a footer but not group selected for e.g. old automated emails
    if (selectedCanSpamId) {
      return groups.flatMap(g => g.canSpamSettings);
    }
    return [];
  }
  const group = groups.find(g => {
    var _g$preferencesGroup;
    return (g === null || g === void 0 || (_g$preferencesGroup = g.preferencesGroup) === null || _g$preferencesGroup === void 0 ? void 0 : _g$preferencesGroup.id) === selectedGroupId;
  });
  if (!group) {
    Raven.captureException(new Error('No group found for selected preferencesGroupId'), {
      extra: {
        selectedGroupId
      }
    });
    return [];
  }
  return group.canSpamSettings;
});
export const getHasAnyValidCanSpamSetting = createSelector([getCanSpamSettings], canSpamSettings => {
  return !!canSpamSettings.find(setting => setting.isValid);
});
export const getCurrentlySelectedCanSpamSetting = createSelector([getCanSpamSettings, getCanSpamSettingsId], (canSpamSettings, canSpamSettingsId) => canSpamSettings.find(setting => setting.id === canSpamSettingsId) || CanSpamSetting({
  name: I18n.text('manageCanSpam.modal.defaultName', {
    number: 0
  })
}));
export const getCanSpamAsString = createSelector([getCurrentlySelectedCanSpamSetting, getLanguage], (canSpamSetting, language) => canSpamSetting && canSpamAsString(canSpamSetting, language));
export const shouldShowSelectForDowngradedPortals = createSelector([getCanSpamSettings, getCanSpamSettingsId, getCanHaveMultipleOfficeLocations], (canSpamSettings, savedId, canHaveMultipleOfficeLocations) => {
  if (canHaveMultipleOfficeLocations) {
    return false;
  }
  const selectedCanSpamExists = canSpamSettings.find(canSpam => canSpam.id === savedId);
  return savedId && !selectedCanSpamExists || !selectedCanSpamExists.isDefault;
});
export const getReplyToDomain = createSelector([getReplyTo], replyTo => {
  if (replyTo.indexOf('@') > -1) {
    return replyTo.replace(/.*@/, '').replace(/"\)\W?}}.*?$/, '');
  }
  return '';
});
export const getIsEmailToThemselves = createSelector([getReplyTo, getSingleRecipient, getUserEmail], (replyTo, recipient, userEmail) => {
  if (replyTo === userEmail && recipient) {
    const properties = recipient.get('properties');
    return properties.email && properties.email.value === replyTo;
  }
  return false;
});
export const getReplyToValidationErrors = createSelector([getFieldErrorMap, getReplyTo, getIsUngatedForBEValidation], (fieldErrorMap, replyTo, isUngatedForBEValidation) => {
  if (isUngatedForBEValidation) {
    var _fieldErrorMap$REPLY_;
    if ((_fieldErrorMap$REPLY_ = fieldErrorMap['REPLY_TO']) !== null && _fieldErrorMap$REPLY_ !== void 0 && _fieldErrorMap$REPLY_.some(e => e.errorKey === 'NOT_SET')) {
      return {
        reply_to: {
          type: 'required',
          pane: 'settings',
          message: I18n.text('editors.replyTo.missing')
        }
      };
    }
    return {};
  } else {
    if (!replyTo.trim().length) {
      return {
        reply_to: {
          pane: 'settings',
          type: 'required',
          message: I18n.text('editors.replyTo.missing')
        }
      };
    }
    return {};
  }
});
export const getCustomReplyToValidationErrors = createSelector([getCustomReplyToEnabled, getCustomReplyTo, getFieldErrorMap, getIsUngatedForBEValidation], (enabled, customReplyTo, fieldErrorMap, isUngatedForBEValidation) => {
  const pane = 'settings';
  if (isUngatedForBEValidation) {
    var _fieldErrorMap$CUSTOM;
    if ((_fieldErrorMap$CUSTOM = fieldErrorMap['CUSTOM_REPLY_TO']) !== null && _fieldErrorMap$CUSTOM !== void 0 && _fieldErrorMap$CUSTOM.some(e => e.errorKey === 'NOT_SET')) {
      return {
        custom_reply_to: {
          type: 'required',
          pane: 'settings',
          message: I18n.text('editors.customReplyTo.missing')
        }
      };
    }
    return {};
  } else {
    if (enabled && !customReplyTo.trim().length) {
      return {
        custom_reply_to: {
          pane,
          type: 'required',
          message: I18n.text('editors.customReplyTo.missing')
        }
      };
    }
    return {};
  }
});
export const getFromNameValidationErrors = createSelector([getFieldErrorMap, getFromName, getIsUngatedForBEValidation], (fieldErrorMap, fromName, isUngatedForBEValidation) => {
  if (isUngatedForBEValidation) {
    var _fieldErrorMap$FROM_N;
    if ((_fieldErrorMap$FROM_N = fieldErrorMap['FROM_NAME']) !== null && _fieldErrorMap$FROM_N !== void 0 && _fieldErrorMap$FROM_N.some(e => e.errorKey === 'NOT_SET')) {
      return {
        from_name: {
          type: 'required',
          pane: 'settings',
          message: I18n.text('editors.fromName.missing')
        }
      };
    }
    return {};
  } else {
    if (!fromName.trim().length) {
      return {
        from_name: {
          pane: 'settings',
          type: 'required',
          message: I18n.text('editors.fromName.missing')
        }
      };
    }
    return {};
  }
});
export const getEmailNameValidationErrors = createSelector([getEmailNameValue], name => {
  if (!name || name.trim().length === 0) {
    return {
      name: {
        type: 'required',
        message: I18n.text('editors.name.missing'),
        pane: 'settings'
      }
    };
  }
  return {};
});
const getDeceptiveSubjectStrings = emailConfigSelector('deceptiveSubjectStrings');
const getDeceptiveSubjectStringsRegex = createSelector([getDeceptiveSubjectStrings], deceptiveSubjectStrings => {
  return {
    DECEPTIVE_FORWARD_REGEX: new RegExp(deceptiveSubjectStrings[1], 'i'),
    DECEPTIVE_REPLY_REGEX: new RegExp(deceptiveSubjectStrings[0], 'i')
  };
});
export const getHasRemovableUnsubscribeElements = emailConfigSelector('hasRemovableUnsubscribeElements');
const getErrorOnSubjectField = (subject, {
  DECEPTIVE_FORWARD_REGEX,
  DECEPTIVE_REPLY_REGEX
}) => {
  const actualSubject = subject.trim();
  const noSubject = actualSubject.length === 0;
  const tooLongSubject = actualSubject.length >= 1000;
  if (noSubject) {
    return {
      type: 'required',
      message: I18n.text('contentmodules.validation.subject.errors.required')
    };
  }
  if (tooLongSubject) {
    return {
      type: 'tooLong',
      max: 1000,
      message: I18n.text('contentmodules.validation.subject.errors.tooLong')
    };
  }
  const deceptiveReply = DECEPTIVE_REPLY_REGEX.exec(actualSubject);
  if (deceptiveReply) {
    return {
      type: 'deceptiveReply',
      message: unescapedText('contentmodules.validation.subject.errors.deceptiveReply', {
        deceptiveReply: deceptiveReply[0]
      })
    };
  }
  const deceptiveForward = DECEPTIVE_FORWARD_REGEX.exec(actualSubject);
  if (deceptiveForward) {
    return {
      type: 'deceptiveForward',
      deceptiveForward: deceptiveForward[0],
      message: unescapedText('contentmodules.validation.subject.errors.deceptiveForward', {
        deceptiveForward: deceptiveForward[0]
      })
    };
  }
  return {};
};
const getSimpleAbSubjectValidationErrors = createSelector([getIsSimpleAbTest, getSubject, getABTest, getDeceptiveSubjectStringsRegex], (isSimpleAb, subject, abTest, deceptiveSubjectStringsRegex) => {
  var _abTest$master, _abTest$variations, _variant$content$subj, _variant$content, _variant$content2;
  if (!isSimpleAb) {
    return null;
  }
  const result = {};
  const masterId = abTest === null || abTest === void 0 || (_abTest$master = abTest.master) === null || _abTest$master === void 0 ? void 0 : _abTest$master.id;
  if (!masterId) {
    return result;
  }
  const variant = abTest === null || abTest === void 0 || (_abTest$variations = abTest.variations) === null || _abTest$variations === void 0 ? void 0 : _abTest$variations.find(v => v.id !== masterId);
  const masterError = getErrorOnSubjectField(subject, deceptiveSubjectStringsRegex);
  if (Object.keys(masterError).length) {
    result.simple_ab_subject_a = Object.assign({}, masterError, {
      pane: 'settings'
    });
  }
  if (!variant) {
    return result;
  }
  const variantError = getErrorOnSubjectField((_variant$content$subj = (_variant$content = variant.content) === null || _variant$content === void 0 ? void 0 : _variant$content.subject) !== null && _variant$content$subj !== void 0 ? _variant$content$subj : '', deceptiveSubjectStringsRegex);
  if (Object.keys(variantError).length) {
    result.simple_ab_subject_b = Object.assign({}, variantError, {
      pane: 'settings'
    });
  }
  const variantSubject = ((_variant$content2 = variant.content) === null || _variant$content2 === void 0 ? void 0 : _variant$content2.subject) || '';
  if (subject && variantSubject && subject.trim() === variantSubject.trim()) {
    result.simple_ab_duplicate_subject = {
      messageKey: 'editors.subject.duplicateSubjectLines',
      target: 'subject',
      pane: 'settings'
    };
  }
  return result;
});
export const getSubjectValidationErrors = createSelector([getSubject, getUseRssHeadlineAsSubject, getDeceptiveSubjectStringsRegex, getSmartSubjectDefinition, getSimpleAbSubjectValidationErrors], (subject, useRssHeadlineAsSubject, deceptiveSubjectStringsRegex, smartDefinition, simpleAbSubjectValidationErrors) => {
  if (simpleAbSubjectValidationErrors !== null) {
    return simpleAbSubjectValidationErrors;
  }
  const pane = 'settings';
  let allErrors = {};
  if (useRssHeadlineAsSubject) {
    return {};
  }
  const defaultSubjectError = getErrorOnSubjectField(subject, deceptiveSubjectStringsRegex);
  if (smartDefinition) {
    // get errors for each smart rule
    let errors = {};
    if (Object.keys(defaultSubjectError).length) {
      errors = {
        ['default']: defaultSubjectError
      };
    }
    const smartSubjectErrors = smartDefinition === null || smartDefinition === void 0 ? void 0 : smartDefinition.smart_objects.reduce((errs, smartRule) => {
      const smartErrors = getErrorOnSubjectField(smartRule === null || smartRule === void 0 ? void 0 : smartRule.value, deceptiveSubjectStringsRegex);
      if (Object.keys(smartErrors).length) {
        return Object.assign({}, errs, {
          [smartRule === null || smartRule === void 0 ? void 0 : smartRule.criterion_id]: smartErrors
        });
      }
      return errs;
    }, errors);
    if (Object.keys(smartSubjectErrors).length) {
      allErrors = {
        type: 'smartEmailFieldErrors',
        smartErrors: smartSubjectErrors
      };
    }
  } else if (Object.keys(defaultSubjectError).length) {
    allErrors = defaultSubjectError;
  }
  if (Object.keys(allErrors).length) {
    allErrors.pane = pane;
    allErrors = {
      subject: allErrors
    };
  }
  return allErrors;
});
export const getCanSpamValidationErrors = createSelector([getIsTransactional, getCanSpamSettingsId, getCanHaveMultipleOfficeLocations, getHasSubscriptionGroupsWriteAccess], (isTransactional, canSpamSettingsId, canHaveMultipleLocations, hasSubscriptionGroupsWriteAccess) => {
  const noCanSpamSelected = !canSpamSettingsId || canSpamSettingsId === -1;
  if (!isTransactional && (canHaveMultipleLocations || hasSubscriptionGroupsWriteAccess) && noCanSpamSelected) {
    return {
      canSpamSettings: {
        pane: 'settings',
        type: 'required',
        message: I18n.text('canSpamSettings.missing')
      }
    };
  }
  return {};
});
export const getThrottleSendSpeedValidationErrors = createSelector([getMaximumRateLimitOption, getHasEmailThrottleSendSpeedAccess, getSendSpeedError], (maximumRateLimitOption, hasEmailThrottleSendSpeedAccess, sendSpeedError) => {
  if (!hasEmailThrottleSendSpeedAccess || maximumRateLimitOption !== STAGGER_EMAIL_SEND_SPEED.THROTTLE || !sendSpeedError) {
    return {};
  }
  const {
    errorType,
    min,
    max
  } = sendSpeedError;
  if (errorType === SEND_SPEED_ERRORS.EXCEEDS_THRESHOLD) {
    return {
      throttle_send_speed: {
        message: I18n.text('maximumRateLimit.validation.exceeds')
      }
    };
  }
  if (errorType === SEND_SPEED_ERRORS.EMPTY) {
    return {
      throttle_send_speed: {
        message: I18n.text('maximumRateLimit.validation.empty')
      }
    };
  }
  if (errorType === SEND_SPEED_ERRORS.BELOW_MIN) {
    return {
      throttle_send_speed: {
        message: I18n.text('maximumRateLimit.validation.min', {
          minEmailsPerMinute: min
        })
      }
    };
  }
  if (errorType === SEND_SPEED_ERRORS.BELOW_DYNAMIC_MIN) {
    return {
      throttle_send_speed: {
        message: I18n.text('maximumRateLimit.validation.dynamicMin', {
          minEmailsPerMinute: min
        })
      }
    };
  }
  if (errorType === SEND_SPEED_ERRORS.ABOVE_MAX) {
    return {
      throttle_send_speed: {
        message: I18n.text('maximumRateLimit.validation.max', {
          maxEmailsPerMinute: max + 1
        })
      }
    };
  }
  return {};
});
export const getTrackingSettings = createSelector(getSiteSettings, settings => {
  return {
    // Track email opens
    emailOpenTrackingAnonymizationEnabled: settings === null || settings === void 0 ? void 0 : settings.emailOpenTrackingAnonymizationEnabled,
    // Source tracking
    utmSourceTracking: settings === null || settings === void 0 ? void 0 : settings.utmSourceTracking,
    // Track clicks in HTML emails
    utmAutomaticTracking: settings === null || settings === void 0 ? void 0 : settings.utmAutomaticTracking,
    // Track clicks in plain text emails
    utmAutomaticPlainTextTracking: settings === null || settings === void 0 ? void 0 : settings.utmAutomaticPlainTextTracking,
    // Identity tracking
    utmIdentityTracking: settings === null || settings === void 0 ? void 0 : settings.utmIdentityTracking
  };
});
export const getAreAllTrackingSettingsEnabled = createSelector(getTrackingSettings, ({
  emailOpenTrackingAnonymizationEnabled,
  utmSourceTracking,
  utmAutomaticTracking,
  utmAutomaticPlainTextTracking,
  utmIdentityTracking
}) => {
  return !emailOpenTrackingAnonymizationEnabled && utmSourceTracking === 'REWRITE_ALL' && utmAutomaticTracking && utmAutomaticPlainTextTracking && utmIdentityTracking;
});
export const getIsTrackingDisabledForThisEmail = createSelector(getContentModel, contentModel => {
  return contentModel.disableAllTracking;
});